/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useEffect, useState } from 'react';
import TargetActual from '../../components/target-actual';
import MediaSlider from '../../components/documents/media-slider';
import { RatingsCarousel, StandardsCarousel } from '../../components/ratings-standards';
import { UniversalTrackersTable } from '../../components/universal-trackers-table';
import { DashboardSectionTitle } from '../../components/dashboard';
import { loadScorecardsByInitiativeId, loadScorecardsBySurveyId } from '../../actions/scorecard';
import { CompanyProfile } from '../../components/company-profile';
import { RootState, useAppDispatch, useAppSelector } from '../../reducers';
import { TABS } from '../../components/target-actual/TargetActualModal';
import { Layout } from './insights/partials/Layout';
import { LayoutColumn, LayoutRow } from './insights/utils/layout';
import { InsightsOverview } from './insights/overview';
import { InsightsProps } from './insightTypes';
import { isSameOrBeforeDate } from '@utils/date';
import config from '../../config';

interface CTProSummaryProps extends InsightsProps {
  getHighlightButton: (column: LayoutColumn) => React.JSX.Element | undefined;
  layout: LayoutRow[];
}

const isTargetActualEnabled = (created?: string) => {
  if (!created) {
    return false;
  }

  return isSameOrBeforeDate(config.targetActualHiddenDate, created);
};

export const Summary = (props: CTProSummaryProps) => {
  const { readOnly, initiativeId, utTableUsage, surveyId, utrTable, getHighlightButton, layout } = props;
  const initiative = useAppSelector((state: RootState) => state.initiative.data);

  const insightsOverviewProps = { ...props, utrTable: undefined };

  const dispatch = useAppDispatch();
  useEffect(() => {
    if (surveyId) {
      dispatch(loadScorecardsBySurveyId(initiativeId, surveyId));
    } else {
      dispatch(loadScorecardsByInitiativeId(initiativeId));
    }
  }, [dispatch, initiativeId, surveyId])

  const [openTargetActualModal, setOpenTargetActualModal] = useState(false)
  const [activeTab, setActiveTab] = useState(TABS.CustomTargets);
  const toggleTargetActualModal = () => {
    setOpenTargetActualModal(!openTargetActualModal)
  }

  return (
    <>
      <CompanyProfile initiative={initiative} readOnly={readOnly} />
      <DashboardSectionTitle title='Highlights' headingStyle={2} className='text-ThemeHeadingLight' />
      <Layout getHighlightButton={getHighlightButton} layout={layout} />
      <InsightsOverview {...insightsOverviewProps} />
      {isTargetActualEnabled(initiative?.created) ? (
        <TargetActual
          openTargetActualModal={openTargetActualModal}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          toggleTargetActualModal={toggleTargetActualModal}
          surveyId={surveyId}
          readOnly={readOnly}
        />
      ) : null}
      {utrTable ? (
        <UniversalTrackersTable
          readOnly={readOnly}
          header={'Targets and Trends'}
          usage={utTableUsage}
          universalTrackers={utrTable}
        />
      ) : null}
      <DashboardSectionTitle
        title='Media, Ratings and Certifications'
        headingStyle={2}
        className='text-ThemeHeadingLight'
      />
      <MediaSlider readOnly={readOnly} initiativeId={initiativeId} />
      <RatingsCarousel
        surveyId={surveyId}
        readOnly={readOnly}
        initiativeId={initiativeId}
        options={{ showInfoButton: true }}
      />
      <StandardsCarousel
        surveyId={surveyId}
        readOnly={readOnly}
        initiativeId={initiativeId}
        options={{ showDownloadButton: true, showInfoButton: true }}
      />
    </>
  );
}
